import Dexie, { type Table, type Transaction } from 'dexie';
import type {
  AppPreferences,
  AuditEvent,
  BackupMetadata,
  DayRecord,
  DayReflection,
  ExerciseSet,
  FocusSession,
  FrenchSession,
  Habit,
  HabitCompletion,
  NovelChapter,
  NovelProject,
  Project,
  ProjectMilestone,
  SchoolSubject,
  SleepRecord,
  StreakRecord,
  StudySession,
  StudyTopic,
  TaskOccurrence,
  TaskProgressEntry,
  TaskTemplate,
  UserSettings,
  WorkoutSession,
  XpEvent,
  YouTubeVideo
} from '@/types';

export const DATABASE_NAME = 'discipline';
export const DATABASE_VERSION = 2;

export const BACKUP_TABLE_NAMES = [
  'userSettings',
  'appPreferences',
  'projects',
  'projectMilestones',
  'taskTemplates',
  'taskOccurrences',
  'taskProgressEntries',
  'focusSessions',
  'dayRecords',
  'dayReflections',
  'habits',
  'habitCompletions',
  'streakRecords',
  'xpEvents',
  'auditEvents',
  'schoolSubjects',
  'studyTopics',
  'studySessions',
  'frenchSessions',
  'workoutSessions',
  'exerciseSets',
  'novelProjects',
  'novelChapters',
  'youtubeVideos',
  'sleepRecords',
  'backupMetadata'
] as const;

export type BackupTableName = (typeof BACKUP_TABLE_NAMES)[number];

const VERSION_1_STORES: Record<BackupTableName, string> = {
  userSettings: 'id, updatedAt',
  appPreferences: 'id, updatedAt',
  projects: 'id, status, area, startDate, targetDate, updatedAt',
  projectMilestones: 'id, projectId, targetDate, completedAt, [projectId+sortOrder]',
  taskTemplates: 'id, category, projectId, effectiveFrom, effectiveUntil, *tags',
  taskOccurrences: 'id, templateId, date, status, category, priority, projectId, [date+status]',
  taskProgressEntries: 'id, occurrenceId, date, kind, focusSessionId',
  focusSessions: 'id, occurrenceId, date, status, [date+status]',
  dayRecords: 'id, date',
  dayReflections: 'id, date',
  habits: 'id, taskTemplateId, category, startedOn',
  habitCompletions: 'id, habitId, date, status, [habitId+date]',
  streakRecords: 'id, habitId, calculatedThrough',
  xpEvents: 'id, sourceKey, sourceType, sourceId, date',
  auditEvents: 'id, entityType, entityId, action, date, [entityType+entityId]',
  schoolSubjects: 'id, projectId, sortOrder',
  studyTopics: 'id, subjectId, parentTopicId, nextReviewOn, understanding',
  studySessions: 'id, subjectId, topicId, date, [subjectId+date]',
  frenchSessions: 'id, date, area, occurrenceId',
  workoutSessions: 'id, date, occurrenceId',
  exerciseSets: 'id, workoutSessionId, exercise, [workoutSessionId+setNumber]',
  novelProjects: 'id, projectId',
  novelChapters: 'id, novelProjectId, chapterNumber, status, [novelProjectId+chapterNumber]',
  youtubeVideos: 'id, projectId, status, publishedOn',
  sleepRecords: 'id, date',
  backupMetadata: 'id, backupId, exportedAt, schemaVersion'
};

const VERSION_2_STORES: Record<BackupTableName, string> = {
  ...VERSION_1_STORES,
  taskTemplates: 'id, category, projectId, effectiveFrom, effectiveUntil, *tags',
  taskOccurrences:
    'id, &occurrenceKey, templateId, date, status, category, priority, projectId, [date+status], [date+category], [templateId+date]',
  taskProgressEntries: 'id, occurrenceId, date, kind, focusSessionId, [occurrenceId+createdAt]',
  dayRecords: 'id, &date, updatedAt',
  dayReflections: 'id, &date, updatedAt',
  habitCompletions: 'id, &completionKey, habitId, date, status, [habitId+date]',
  streakRecords: 'id, &habitId, calculatedThrough',
  xpEvents: 'id, &sourceKey, sourceType, sourceId, date, [date+sourceType]',
  sleepRecords: 'id, &date',
  backupMetadata: 'id, &backupId, exportedAt, schemaVersion'
};

interface LegacyTaskOccurrence extends Omit<Partial<TaskOccurrence>, 'date'> {
  id: string;
  date?: string;
  templateId?: string;
}

interface LegacyHabitCompletion extends Omit<Partial<HabitCompletion>, 'date'> {
  id: string;
  habitId?: string;
  date?: string;
}

async function migrateToVersion2(transaction: Transaction): Promise<void> {
  await transaction
    .table<LegacyTaskOccurrence, string>('taskOccurrences')
    .toCollection()
    .modify((occurrence) => {
      occurrence.occurrenceKey ??=
        occurrence.templateId && occurrence.date
          ? `${occurrence.templateId}:${occurrence.date}`
          : `legacy:${occurrence.id}`;
      occurrence.currentValue ??= 0;
    });
  await transaction
    .table<LegacyHabitCompletion, string>('habitCompletions')
    .toCollection()
    .modify((completion) => {
      completion.completionKey ??=
        completion.habitId && completion.date
          ? `${completion.habitId}:${completion.date}`
          : `legacy:${completion.id}`;
    });
}

export class DisciplineDatabase extends Dexie {
  userSettings!: Table<UserSettings, string>;
  appPreferences!: Table<AppPreferences, string>;
  projects!: Table<Project, string>;
  projectMilestones!: Table<ProjectMilestone, string>;
  taskTemplates!: Table<TaskTemplate, string>;
  taskOccurrences!: Table<TaskOccurrence, string>;
  taskProgressEntries!: Table<TaskProgressEntry, string>;
  focusSessions!: Table<FocusSession, string>;
  dayRecords!: Table<DayRecord, string>;
  dayReflections!: Table<DayReflection, string>;
  habits!: Table<Habit, string>;
  habitCompletions!: Table<HabitCompletion, string>;
  streakRecords!: Table<StreakRecord, string>;
  xpEvents!: Table<XpEvent, string>;
  auditEvents!: Table<AuditEvent, string>;
  schoolSubjects!: Table<SchoolSubject, string>;
  studyTopics!: Table<StudyTopic, string>;
  studySessions!: Table<StudySession, string>;
  frenchSessions!: Table<FrenchSession, string>;
  workoutSessions!: Table<WorkoutSession, string>;
  exerciseSets!: Table<ExerciseSet, string>;
  novelProjects!: Table<NovelProject, string>;
  novelChapters!: Table<NovelChapter, string>;
  youtubeVideos!: Table<YouTubeVideo, string>;
  sleepRecords!: Table<SleepRecord, string>;
  backupMetadata!: Table<BackupMetadata, string>;

  constructor(name = DATABASE_NAME) {
    super(name);
    this.version(1).stores(VERSION_1_STORES);
    this.version(2).stores(VERSION_2_STORES).upgrade(migrateToVersion2);
  }
}

export const db = new DisciplineDatabase();
